// src/hooks/useForm.tsx
import { useState, useEffect } from "react";
import { FormData, FORM_STEPS } from "../../types";
import {
  calculateAccidentDate,
  formatDateWithoutTime,
  validateCaseDescription,
  validateEmail,
  validateName,
  validatePhone,
} from "../../utils/helper";
import {
  submitQualifiedLead,
  submitDequalifiedLead,
  triggerEmailStepZapier,
} from "../services/api";

const initialFormData: FormData = {
  ProductId: 45,
  treatedBy: "",
  physicalInjury: "",
  hasAttorney: "",
  accidentFault: "",
  caseDescription: "",
  date: null,
  zip: 0,
  state: "",
  userIp: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  Years: -1,
  source: "",
  interestScale: "SCALE3",
  apiId: "2C01DACAA77C11EABBAB42010A5C50F3",
  apiPassword: "bea8049",
  webSiteUrl:
    "https://clients.caraccidentcounsel.com/s3/Spanish-Youtubehttps://clients.caraccidentcounsel.com/s3/tiktok.spanish",
  tcpa: "YES",
  accidentType: "CAR",
  tcpaText:
    "By clicking the next button I give consent to receive emails, calls, SMS/text messages, and communications from Car Accident Counsel and its network of lawyers/advocates to any number or email address given by me.",
  price: "0",
  userAgent: navigator.userAgent,
  leadLanguage: "English",
  isDequalified: false,
  dequalReason: "",
  trustedFormUrl: "", // Add this field to store the TrustedForm URL
  jornayaLeadId: "", // Add this field to store the LeadID
};

interface ValidationState {
  caseDescription: { valid: boolean; error: string };
  email: { valid: boolean; error: string };
  phone: { valid: boolean; error: string };
  firstName: { valid: boolean; error: string };
  lastName: { valid: boolean; error: string };
}

const initialValidation: ValidationState = {
  caseDescription: { valid: true, error: "" },
  email: { valid: true, error: "" },
  phone: { valid: true, error: "" },
  firstName: { valid: true, error: "" },
  lastName: { valid: true, error: "" },
};

export const useForm = (
  funnelSource: string | undefined,
  language: string,
  sourceNumber?: number,
  defaultValues?: {
    physicalInjury?: string;
    treatedBy?: string;
    hasAttorney?: string;
    accidentFault?: string;
  }
) => {
  // Determine initial step based on sourceNumber
  const getInitialStep = () => {
    if (sourceNumber === 6) {
      return FORM_STEPS.DETAILS; // Skip first 3 steps for source 6
    }
    return FORM_STEPS.INJURY; // Default starting step
  };

  // Initialize form data with default values if provided
  const getInitialFormData = () => {
    const baseFormData = {
      ...initialFormData,
      source: funnelSource || "native.paid",
      leadLanguage: language === "sp" ? "Spanish" : "English",
    };

    if (sourceNumber === 6 && defaultValues) {
      return {
        ...baseFormData,
        physicalInjury: defaultValues.physicalInjury || "",
        treatedBy: defaultValues.treatedBy || "",
        hasAttorney: defaultValues.hasAttorney || "",
        accidentFault: defaultValues.accidentFault || "",
      };
    }

    return baseFormData;
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [formValidation, setFormValidation] =
    useState<ValidationState>(initialValidation);
  const [currentStep, setCurrentStep] = useState<number>(getInitialStep());
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Update userIp and zip when they become available from lead tracking
  useEffect(() => {
    // This will be called from the main component
    // to update the form data with lead tracking values
  }, []);

  // Check if user should be dequalified
  const checkDequalification = (field: string, value: string) => {
    // Skip dequalification checks for source 6 on the first 3 fields
    // since they have default values
    if (sourceNumber === 6) {
      if (
        field === "pyhsicalInjury" ||
        field === "treatedBy" ||
        field === "hasAttorney" ||
        field === "accidentFault"
      ) {
        return { dequalified: false, reason: "" };
      }
    }

    // Check if the user is not injured
    if (field === "treatedBy" && value === "NO") {
      return { dequalified: true, reason: "Not injured" };
    } else if (field === "hasAttorney" && value === "YES") {
      return { dequalified: true, reason: "Already has attorney" };
    } else if (field === "accidentFault" && value === "YES") {
      return { dequalified: true, reason: "At fault" };
    }
    // Default case - not dequalified
    return { dequalified: false, reason: "" };
  };

  // Get years value based on date selection
  const getYearsValue = (selection: string): number => {
    switch (selection) {
      case "lessthan3monthsCA":
        return 0;
      case "lessthan6monthsCA":
        return 1;
      case "lessthanyearCA":
        return 2;
      case "within2yearsCA":
        return 3;
      case "moreThan2yearsCA":
        return 4;
      case "lessthanyear":
        return 0;
      case "within2years":
        return 1;
      case "moreThan2years":
        return 2;
      default:
        return -1;
    }
  };

  // Function to update form data with lead tracking information
  const updateFormWithLeadTracking = (trackingData: {
    userIp: string;
    zipCode: number;
    state: string;
    trustedFormUrl: string;
    jornayaLeadId: string;
  }) => {
    // Only update if values actually differ from current form data to avoid loops
    if (
      trackingData.userIp !== formData.userIp ||
      trackingData.zipCode !== formData.zip ||
      trackingData.state !== formData.state ||
      trackingData.trustedFormUrl !== formData.trustedFormUrl ||
      trackingData.jornayaLeadId !== formData.jornayaLeadId
    ) {
      setFormData((prevData) => ({
        ...prevData,
        userIp: trackingData.userIp || prevData.userIp,
        zip: trackingData.zipCode || prevData.zip,
        state: trackingData.state || prevData.state,
        trustedFormUrl: trackingData.trustedFormUrl || prevData.trustedFormUrl,
        jornayaLeadId: trackingData.jornayaLeadId || prevData.jornayaLeadId,
      }));
    }
  };

  // Handle input changes only (no auto-advance)
  const handleInputChange = (field: string, value: string) => {
    let validationResult = { valid: true, error: "" };

    // Check if this selection should dequalify the user
    const dequalCheck = checkDequalification(field, value);

    // Validate fields
    if (field === "caseDescription") {
      validationResult = validateCaseDescription(value);
    } else if (field === "email") {
      validationResult = validateEmail(value);
    } else if (field === "phone") {
      validationResult = validatePhone(value);
    } else if (field === "firstName") {
      validationResult = validateName(value, "First name");
    } else if (field === "lastName") {
      validationResult = validateName(value, "Last name");
    }

    setFormValidation((prev) => ({
      ...prev,
      [field]: validationResult,
    }));

    // Update the form data without auto-advancing
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(dequalCheck.dequalified
        ? {
            isDequalified: true,
            dequalReason: dequalCheck.reason,
          }
        : {}),
    }));
  };

  // Handle step submission (explicitly advancing to next step)
  const handleStepSubmit = (
    field: string,
    value: string,
    certificateUrl: string = "",
    leadId: string = "",
    userIp: string = "",
    zipCode: number = 0,
    state: string = ""
  ) => {
    // Update the tracking data in form if provided
    if (certificateUrl || leadId || userIp || zipCode) {
      updateFormWithLeadTracking({
        trustedFormUrl: certificateUrl,
        jornayaLeadId: leadId,
        userIp: userIp || formData.userIp,
        zipCode: zipCode || formData.zip,
        state: state || formData.state,
      });
    }

    // For certain button choices, we still want to auto-advance
    const isButtonSelection = [
      "treatedBy",
      "physicalInjury",
      "hasAttorney",
      "accidentFault",
    ].includes(field);

    if (field === "accidentDateSelection") {
      // Calculate the accident date
      const accidentDate = calculateAccidentDate(value);
      const yearsValue = getYearsValue(value);
      const immediateSubmit = value === "moreThan2yearsCA";

      // Update form data
      const updatedFormData = {
        ...formData,
        date: accidentDate,
        dateSelection: value,
        Years: yearsValue,
        trustedFormUrl: certificateUrl || formData.trustedFormUrl,
        jornayaLeadId: leadId || formData.jornayaLeadId,
        userIp: userIp || formData.userIp,
        zip: zipCode || formData.zip,
        state: state || formData.state,
        isDequalified: immediateSubmit || formData.isDequalified,
        dequalReason: immediateSubmit
          ? "More than 2 years"
          : formData.dequalReason,
      };

      setFormData(updatedFormData);

      // Slight delay before action
      setTimeout(() => {
        if (immediateSubmit) {
          handleSubmit(updatedFormData, certificateUrl, leadId, true);
        } else {
          setCurrentStep(FORM_STEPS.FIRST_NAME);
        }
      }, 300);
    } else if (field === "email") {
      // Email step completed - trigger Zapier call (non-blocking)
      const { phone, ...emailStepData } = {
        ...formData,
        email: value,
        trustedFormUrl: certificateUrl || formData.trustedFormUrl,
        jornayaLeadId: leadId || formData.jornayaLeadId,
        userIp: userIp || formData.userIp,
        zip: zipCode || formData.zip,
        state: state || formData.state,
      };

      // Trigger the non-blocking Zapier call (phone property excluded)
      triggerEmailStepZapier(emailStepData);

      // Continue to next step
      goToNextStep();
    } else if (field === "phone") {
      // Final step - submit the form
      handleSubmit(
        {
          ...formData,
          phone: value,
          trustedFormUrl: certificateUrl || formData.trustedFormUrl,
          jornayaLeadId: leadId || formData.jornayaLeadId,
          userIp: userIp || formData.userIp,
          zip: zipCode || formData.zip,
          state: state || formData.state,
        },
        certificateUrl,
        leadId
      );
    } else {
      // For button selections or explicit next button clicks, go to next step
      goToNextStep();
    }
  };

  // Submit form data
  const handleSubmit = async (
    dataToSubmit: FormData = formData,
    certUrl: string = "",
    universalLeadId: string = "",
    redirectImmediately: boolean = false
  ) => {
    try {
      setIsSubmitting(true);
      // console.log("Form submission started with data:", dataToSubmit);

      // If this is a moreThan2years submission, make sure years and dateSelection are correct
      const finalData =
        dataToSubmit.dateSelection === "moreThan2yearsCA"
          ? { ...dataToSubmit, years: 4, dateSelection: "moreThan2yearsCA" }
          : dataToSubmit;

      // Ensure we have the trusted form and lead ID
      const trustedFormFinal = certUrl || dataToSubmit.trustedFormUrl || "";
      const leadIdFinal = universalLeadId || dataToSubmit.jornayaLeadId || "";

      // console.log("Using trustedFormUrl:", trustedFormFinal);
      // console.log("Using jornayaLeadId:", leadIdFinal);
      // console.log("Using userIp:", dataToSubmit.userIp);
      // console.log("Using zip:", dataToSubmit.zip);

      const dataToSend = {
        ...finalData,
        date: formatDateWithoutTime(finalData.date),
        trustedFormUrl: trustedFormFinal,
        jornayaLeadId: leadIdFinal,
      };

      // Remove temp fields before sending
      delete dataToSend.dateSelection;
      delete dataToSend.isDequalified;
      delete dataToSend.dequalReason;

      console.log("Final data to send:", dataToSend);

      // Redirect immediately if specified
      if (redirectImmediately) {
        window.location.href =
          "https://www.caraccidentcounsel.com/claim/survey-thank-you-dq.php";
        return;
      }

      // Do a final check for empty values
      if (!dataToSend.trustedFormUrl) {
        console.warn("Warning: trustedFormUrl is empty in submission!");

        // Try one last time to get it from the DOM
        const certInput = document.querySelector(
          'input[name="xxTrustedFormCertUrl1"]'
        ) as HTMLInputElement;

        if (certInput && certInput.value) {
          dataToSend.trustedFormUrl = certInput.value;
          // console.log("Found trustedFormUrl in DOM:", certInput.value);
        }
      }

      if (!dataToSend.jornayaLeadId) {
        console.warn("Warning: jornayaLeadId is empty in submission!");

        // Try one last time to get it from the DOM
        const leadIdInput = document.querySelector(
          "#leadid_token"
        ) as HTMLInputElement;

        if (leadIdInput && leadIdInput.value) {
          dataToSend.jornayaLeadId = leadIdInput.value;
          // console.log("Found jornayaLeadId in DOM:", leadIdInput.value);
        }
      }

      // Send to the appropriate endpoint
      if (dataToSubmit.isDequalified) {
        await submitDequalifiedLead(dataToSend);
      } else {
        await submitQualifiedLead(dataToSend);
      }
    } catch (error) {
      setSubmitError("An error occurred. Please try again.");
      console.error("Form submission error:", error);
      setIsSubmitting(false);
    }
  };

  // Move to next step - updated for source 6 logic
  const goToNextStep = () => {
    // For source 6, skip the first 3 steps
    if (sourceNumber === 6) {
      if (currentStep === FORM_STEPS.DETAILS) {
        setCurrentStep(FORM_STEPS.WHEN);
      } else if (currentStep === FORM_STEPS.FIRST_NAME) {
        setCurrentStep(FORM_STEPS.LAST_NAME);
      } else if (currentStep === FORM_STEPS.LAST_NAME) {
        setCurrentStep(FORM_STEPS.EMAIL);
      } else if (currentStep === FORM_STEPS.EMAIL) {
        setCurrentStep(FORM_STEPS.PHONE);
      }
    } else {
      // Normal flow for other sources
      if (currentStep === FORM_STEPS.INJURY) {
        setCurrentStep(FORM_STEPS.REPRESENTATION);
      } else if (currentStep === FORM_STEPS.REPRESENTATION) {
        setCurrentStep(FORM_STEPS.FAULT);
      } else if (currentStep === FORM_STEPS.FAULT) {
        setCurrentStep(FORM_STEPS.DETAILS);
      } else if (currentStep === FORM_STEPS.DETAILS) {
        setCurrentStep(FORM_STEPS.WHEN);
      } else if (currentStep === FORM_STEPS.FIRST_NAME) {
        setCurrentStep(FORM_STEPS.LAST_NAME);
      } else if (currentStep === FORM_STEPS.LAST_NAME) {
        setCurrentStep(FORM_STEPS.EMAIL);
      } else if (currentStep === FORM_STEPS.EMAIL) {
        setCurrentStep(FORM_STEPS.PHONE);
      }
    }
  };

  return {
    formData,
    formValidation,
    currentStep,
    isSubmitting,
    submitError,
    handleInputChange,
    handleStepSubmit,
    handleSubmit,
    goToNextStep,
    updateFormWithLeadTracking,
  };
};
